import { useEffect, useState } from 'react'
import { marketAPI } from '../services/api'

/** How often an open call's price is re-read: the ticker's server cache is ~60s. */
export const LIVE_PRICE_REFRESH_MS = 30_000

/**
 * The last traded price for ``symbol`` from the public ticker tape
 * (GET /api/market/ticker: no login, cached server-side, priced on the
 * engine's venue, Kraken, whose universe covers every callable coin),
 * re-read every LIVE_PRICE_REFRESH_MS while ``enabled``. Null until a price
 * comes back, and again whenever a read fails or the tape has no row for the
 * symbol: a missing price is never shown as an old one.
 */
const useLivePrice = (symbol: string | null | undefined, enabled: boolean): number | null => {
  const [price, setPrice] = useState<number | null>(null)

  useEffect(() => {
    setPrice(null)
    if (!enabled || !symbol) return undefined
    let active = true
    const read = () =>
      marketAPI
        .getTicker()
        .then((tape) => {
          if (!active) return
          const row = tape.tickers.find((t) => t.symbol === symbol)
          setPrice(row && Number.isFinite(row.price) && row.price > 0 ? row.price : null)
        })
        .catch(() => {
          if (active) setPrice(null)
        })
    read()
    const timer = window.setInterval(read, LIVE_PRICE_REFRESH_MS)
    return () => {
      active = false
      window.clearInterval(timer)
    }
  }, [symbol, enabled])

  return price
}

export default useLivePrice
